import { Field } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import TextInputField from "../formik/text-input";
import CountryCodeDropdown from "./dropdown";
import { PhoneProps } from "./types";
import { BootstrapperContext } from "../../../../context/bootstrapperContext";
import { MaskedInput } from "../../../../ui/widgets/masked-input";
import "./styles.scss";
import { FieldProps } from "formik/dist/Field";
import { InputBaseComponentProps } from "@mui/material";

const classNameBase = "scd-phone";

const CustomMaskedInputSwiss = React.forwardRef<
  HTMLInputElement,
  InputBaseComponentProps
>((props, ref) => {
  return (
    <MaskedInput
      {...props}
      className={`${classNameBase}-masked-input`}
      ref={ref}
      mask="00 000 00 00"
      placeholder="__ ___ ____"
      lazy={false}
    />
  );
});
CustomMaskedInputSwiss.displayName = "CustomMaskedInputSwiss";

const CustomMaskedInputNonSwiss = React.forwardRef<
  HTMLInputElement,
  InputBaseComponentProps
>((props, ref) => {
  return (
    <MaskedInput
      {...props}
      className={`${classNameBase}-masked-input`}
      ref={ref}
      mask={/^\d*$/}
    />
  );
});
CustomMaskedInputNonSwiss.displayName = "CustomMaskedInputNonSwiss";

export default function Phone({
  name,
  dataPath = "",
  value,
  forCompany = false,
  isMobile = false,
  isPrivate = true,
  disabled = false,
  ...other
}: PhoneProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { countries } = useContext(BootstrapperContext);

  let phoneKeyAndNameSelector = "";
  if (forCompany) {
    phoneKeyAndNameSelector = "phone";
  } else {
    phoneKeyAndNameSelector = `${
      isMobile ? "mobile" : `phone${isPrivate ? "Private" : "Business"}`
    }`;
  }

  const isSwissPhone = value && value.code === "+41";

  return (
    <div className={`${classNameBase}-container`}>
      <Field
        name={`${dataPath}${phoneKeyAndNameSelector}.code`}
        key={`${dataPath}${phoneKeyAndNameSelector}.code`}
      >
        {({ form }: FieldProps) => {
          const updateFormikPhoneNumber = (
            phoneCode: string | number | string[],
          ) => {
            if (phoneCode === "+41") {
              form.setFieldValue(
                `${dataPath}${phoneKeyAndNameSelector}.number`,
                value.number.replace(
                  /(\d{2})(\d{3})(\d{2})(\d{2})+/,
                  "$1 $2 $3 $4",
                ),
              );
            } else {
              form.setFieldValue(
                `${dataPath}${phoneKeyAndNameSelector}.number`,
                value.number.replace(/ |_/g, ""),
              );
            }
          };
          return (
            <div className="scd-phone-select-wrapper">
              <CountryCodeDropdown
                onSelectCallback={(event) => {
                  const phoneCode = event.target.value;
                  form.setFieldValue(
                    `${dataPath}${phoneKeyAndNameSelector}.code`,
                    phoneCode,
                  );
                  updateFormikPhoneNumber(phoneCode);
                }}
                selectedValue={value && value.code}
                dataPath={dataPath}
                selector={phoneKeyAndNameSelector}
                countries={countries}
                disabled={disabled}
                className="scd-country-dropdown"
              />
            </div>
          );
        }}
      </Field>
      <div className="scd-phone-text-wrapper">
        <TextInputField
          forceReRender
          CustomInputComponent={
            isSwissPhone ? CustomMaskedInputSwiss : CustomMaskedInputNonSwiss
          }
          InputProps={{
            value: value && value.number,
          }}
          name={name || `${dataPath}${phoneKeyAndNameSelector}.number`}
          label={getMessage(
            `${forCompany ? "Company." : "Person."}${phoneKeyAndNameSelector}`,
          )}
          value={value && value.number}
          disabled={disabled}
          shrink={true}
          type="tel"
          {...other}
        />
      </div>
    </div>
  );
}
